import { FaCodeCompare } from "react-icons/fa6";
import { useAuth } from "../../context/AuthContext";
import { Card, Button, Tag, Row, Col, Checkbox, Tabs, Input, message } from "antd";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSessionsByUserId } from "../../api/SessionsAPI";
import { useCohortsByUserId, useCohortSessions, useSearchArchivedSessionsByUsername } from "../../api/CohortsAPI";
import { useCompetenciesCount } from "../../api/CompetenciesAPI";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import PageBanner from "../../components/common/PageBanner";
import { SessionTypeTag } from "../../components/common/SessionTags";
import { agGridThemeVars, agGridDefaultColDef } from "../../constants/agGridTheme";

const ComparisonList = () => {
    const { currentUser, loading } = useAuth();
    const navigate = useNavigate();
    const [selectedSessions, setSelectedSessions] = useState([]);
    const [selectedCohorts, setSelectedCohorts] = useState([]);
    const [activeTab, setActiveTab] = useState("sessions");
    const [viewingCohortSessions, setViewingCohortSessions] = useState(false);
    const [selectedCohortForSessions, setSelectedCohortForSessions] = useState(null);
    const [selectedCohortSessions, setSelectedCohortSessions] = useState([]);
    const [searchUsername, setSearchUsername] = useState("");
    const [searchResults, setSearchResults] = useState(null);
    const [selectedSearchSessions, setSelectedSearchSessions] = useState([]);
    const searchArchivedSessions = useSearchArchivedSessionsByUsername();

    const {
        data: completedSessions,
        isLoading: sessionsLoading,
        error: sessionsError,
    } = useSessionsByUserId(currentUser?.id, {
        status: "completed",
        includeCompletedCount: true,
    });

    const {
        data: allCohorts,
        isLoading: cohortsLoading,
        error: cohortsError,
    } = useCohortsByUserId(currentUser?.id);

    // Filter to only show archived cohorts
    const completedCohorts = allCohorts?.filter(cohort => cohort.status === 'archived') || [];

    // Fetch sessions for the selected cohort
    const {
        data: cohortSessionsData,
        isLoading: cohortSessionsLoading,
        isFetching: cohortSessionsFetching,
        error: cohortSessionsError,
        refetch: refetchCohortSessions,
    } = useCohortSessions(selectedCohortForSessions?.id);

    // Fetch total competencies count for proper percentage calculation
    const {
        data: totalCompetenciesInDb,
        isLoading: competenciesCountLoading
    } = useCompetenciesCount();

    if (loading || sessionsLoading || cohortsLoading || competenciesCountLoading) {
        return <div>Loading...</div>;
    }

    if (!currentUser) {
        return <div>Please log in to compare sessions</div>;
    }

    // `cohortSessionsError` is intentionally excluded here: it's scoped to
    // whichever single cohort is currently being viewed (see below), and
    // shouldn't take down the whole page. If this query key previously
    // errored (e.g. a stale/cached 401 from before auth was ready) and is now
    // being retried in the background, `error` stays set on the cached
    // result until the retry resolves — showing it here would flash a
    // page-wide error even though the retry is about to succeed.
    if (sessionsError || cohortsError) {
        return <div>Error loading data: {sessionsError?.message || cohortsError?.message}</div>;
    }


    // Handle session selection
    const handleSessionSelection = (sessionId, checked) => {
        if (checked) {
            if (selectedSessions.length < 2) {
                setSelectedSessions([...selectedSessions, sessionId]);
            }
        } else {
            setSelectedSessions(selectedSessions.filter(id => id !== sessionId));
        }
    };

    // Handle cohort selection
    const handleCohortSelection = (cohortId, checked) => {
        if (checked) {
            if (selectedCohorts.length < 2) {
                setSelectedCohorts([...selectedCohorts, cohortId]);
            }
        } else {
            setSelectedCohorts(selectedCohorts.filter(id => id !== cohortId));
        }
    };

    // Handle viewing cohort sessions
    const handleViewCohortSessions = (cohort) => {
        setSelectedCohortForSessions(cohort);
        setViewingCohortSessions(true);
        setSelectedCohortSessions([]); // Reset selected sessions
    };

    // Handle back to cohort list
    const handleBackToCohorts = () => {
        setViewingCohortSessions(false);
        setSelectedCohortForSessions(null);
        setSelectedCohortSessions([]);
    };

    // Handle cohort session selection
    const handleCohortSessionSelection = (sessionId, checked) => {
        if (checked) {
            if (selectedCohortSessions.length < 2) {
                setSelectedCohortSessions([...selectedCohortSessions, sessionId]);
            }
        } else {
            setSelectedCohortSessions(selectedCohortSessions.filter(id => id !== sessionId));
        }
    };

    // Handle participant username search across archived cohorts
    const handleSearchByUsername = (usernameOverride) => {
        const username = (usernameOverride ?? searchUsername).trim();
        if (!username) return;

        searchArchivedSessions.mutate(
            { userId: currentUser.id, username },
            {
                onSuccess: (results) => {
                    setSearchResults(results);
                    setSelectedSearchSessions([]);
                },
                onError: (error) => {
                    message.error(error?.response?.data?.error || "Unable to search sessions.");
                },
            }
        );
    };

    // Jump to the search tab and search for a participant selected directly
    // from a cohort's session list, instead of typing their username in.
    const handleFindParticipantInOtherCohorts = (username) => {
        setSearchUsername(username);
        setActiveTab("search");
        handleSearchByUsername(username);
    };

    // Handle search-result session selection
    const handleSearchSessionSelection = (sessionId, checked) => {
        if (checked) {
            if (selectedSearchSessions.length < 2) {
                setSelectedSearchSessions([...selectedSearchSessions, sessionId]);
            }
        } else {
            setSelectedSearchSessions(selectedSearchSessions.filter(id => id !== sessionId));
        }
    };

    // Table renderers
    const sessionTypeRenderer = (params) => (
        <SessionTypeTag isCohortSession={params.data.cohort_id !== null} />
    );

    const sessionCheckboxRenderer = (params) => {
        const isSelected = selectedSessions.includes(params.data.id);
        const isDisabled = !isSelected && selectedSessions.length >= 2;

        return (
            <Checkbox
                checked={isSelected}
                disabled={isDisabled}
                onChange={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSessionSelection(params.data.id, e.target.checked);
                }}
            />
        );
    };

    const cohortButtonRenderer = (params) => {
        const isSelected = selectedCohorts.includes(params.data.id);
        const isDisabled = !isSelected && selectedCohorts.length >= 2;

        return (
            <Button
                type="primary"
                size="small"
                disabled={isDisabled}
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleViewCohortSessions(params.data);
                }}
            >
                Select Sessions
            </Button>
        );
    };

    const cohortSessionCheckboxRenderer = (params) => {
        const isSelected = selectedCohortSessions.includes(params.data.id);
        const isDisabled = !isSelected && selectedCohortSessions.length >= 2;

        return (
            <Checkbox
                checked={isSelected}
                disabled={isDisabled}
                onChange={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleCohortSessionSelection(params.data.id, e.target.checked);
                }}
            />
        );
    };

    const searchSessionCheckboxRenderer = (params) => {
        const isSelected = selectedSearchSessions.includes(params.data.id);
        const isDisabled = !isSelected && selectedSearchSessions.length >= 2;

        return (
            <Checkbox
                checked={isSelected}
                disabled={isDisabled}
                onChange={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSearchSessionSelection(params.data.id, e.target.checked);
                }}
            />
        );
    };

    // Column definitions for sessions
    const sessionColumnDefs = [
        {
            headerName: "Select",
            cellRenderer: sessionCheckboxRenderer,
            sortable: false,
            filter: false,
            width: 80,
            pinned: "left"
        },
        { headerName: "ID", field: "id", width: 100 },
        {
            headerName: "Started At",
            field: "created_at",
            valueFormatter: (params) => new Date(params.value).toLocaleString(),
            width: 180
        },
        {
            headerName: "Session Type",
            field: "cohort_id",
            cellRenderer: sessionTypeRenderer,
            width: 130
        },
        { headerName: "Competencies", field: "completed_count", width: 130 }
    ];

    // Column definitions for cohorts
    const cohortColumnDefs = [
        {
            headerName: "Action",
            cellRenderer: cohortButtonRenderer,
            sortable: false,
            filter: false,
            width: 140,
            pinned: "left"
        },
        { headerName: "ID", field: "id", width: 100 },
        { headerName: "Name", field: "name", width: 200 },
        {
            headerName: "Created At",
            field: "created_at",
            valueFormatter: (params) => new Date(params.value).toLocaleString(),
            width: 180
        },
        {
            headerName: "Status",
            field: "status",
            cellRenderer: (params) => (
                <Tag color={params.value === "archived" ? "gray" : "green"} className="!text-xs">
                    {params.value}
                </Tag>
            ),
            width: 100
        }
    ];

    // Column definitions for cohort sessions
    const cohortSessionColumnDefs = [
        {
            headerName: "Select",
            cellRenderer: cohortSessionCheckboxRenderer,
            sortable: false,
            filter: false,
            width: 80,
            pinned: "left"
        },
        {
            field: "username",
            headerName: "Participant",
            flex: 3,
            minWidth: 120,
            cellRenderer: (params) => {
                const { username } = params.data;
                return (
                    <div className="flex items-center">
                        <span className="font-medium">{username}</span>
                    </div>
                );
            },
        },
        {
            headerName: "",
            flex: 1.5,
            minWidth: 160,
            sortable: false,
            filter: false,
            cellRenderer: (params) => (
                <Button
                    type="link"
                    size="small"
                    className="!px-0"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleFindParticipantInOtherCohorts(params.data.username);
                    }}
                >
                    Find in other cohorts
                </Button>
            ),
        },
        {
            field: "completed_competencies",
            headerName: "Competencies",
            flex: 1.5,
            minWidth: 110,
            cellRenderer: (params) => {
                const { completed_competencies } = params.data;
                const percentage = totalCompetenciesInDb > 0 ? Number(((completed_competencies / totalCompetenciesInDb) * 100).toFixed(3)) : 0;
                return `${completed_competencies} / ${totalCompetenciesInDb} (${percentage}%)`;
            },
        },
        {
            field: "is_completed",
            headerName: "Status",
            flex: 1.5,
            minWidth: 100,
            cellRenderer: (params) => (
                <span
                    className={`px-2 py-1 rounded-full text-xs ${
                        params.value
                            ? "bg-green-100 text-green-800"
                            : "bg-yellow-100 text-yellow-800"
                    }`}
                >
                    {params.value ? "Completed" : "In Progress"}
                </span>
            ),
        },
        {
            headerName: "Started",
            field: "created_at",
            valueFormatter: (params) => new Date(params.value).toLocaleString(),
            flex: 2,
            minWidth: 150,
        },
    ];

    // Column definitions for username search results (spans multiple cohorts)
    const searchSessionColumnDefs = [
        {
            headerName: "Select",
            cellRenderer: searchSessionCheckboxRenderer,
            sortable: false,
            filter: false,
            width: 80,
            pinned: "left"
        },
        {
            field: "username",
            headerName: "Participant",
            flex: 2,
            minWidth: 130,
        },
        {
            field: "cohort_name",
            headerName: "Cohort",
            flex: 2,
            minWidth: 150,
            valueFormatter: (params) => params.value || "Untitled Cohort",
        },
        {
            field: "completed_competencies",
            headerName: "Competencies",
            flex: 1.5,
            minWidth: 110,
            cellRenderer: (params) => {
                const { completed_competencies } = params.data;
                const percentage = totalCompetenciesInDb > 0 ? Number(((completed_competencies / totalCompetenciesInDb) * 100).toFixed(3)) : 0;
                return `${completed_competencies} / ${totalCompetenciesInDb} (${percentage}%)`;
            },
        },
        {
            field: "is_completed",
            headerName: "Status",
            flex: 1.5,
            minWidth: 100,
            cellRenderer: (params) => (
                <span
                    className={`px-2 py-1 rounded-full text-xs ${
                        params.value
                            ? "bg-green-100 text-green-800"
                            : "bg-yellow-100 text-yellow-800"
                    }`}
                >
                    {params.value ? "Completed" : "In Progress"}
                </span>
            ),
        },
        {
            headerName: "Started",
            field: "created_at",
            valueFormatter: (params) => new Date(params.value).toLocaleString(),
            flex: 2,
            minWidth: 150,
        },
    ];

    // Get selected session details
    const selectedSessionDetails = completedSessions?.filter(session =>
        selectedSessions.includes(session.id)
    ) || [];

    // Get selected cohort details
    const selectedCohortDetails = completedCohorts?.filter(cohort =>
        selectedCohorts.includes(cohort.id)
    ) || [];

    // Get selected cohort session details
    const selectedCohortSessionDetails = cohortSessionsData?.filter(session =>
        selectedCohortSessions.includes(session.id)
    ) || [];

    // Get selected email-search session details
    const selectedSearchSessionDetails = searchResults?.filter(session =>
        selectedSearchSessions.includes(session.id)
    ) || [];

    const handleCompareSessions = () => {
        if (selectedSessions.length === 2) {
            navigate(`/comparison?session1=${selectedSessions[0]}&session2=${selectedSessions[1]}`);
        }
    };

    const handleCompareCohorts = () => {
        if (selectedCohorts.length === 2) {
            navigate(`/comparison?cohort1=${selectedCohorts[0]}&cohort2=${selectedCohorts[1]}`);
        }
    };

    const handleCompareCohortSessions = () => {
        if (selectedCohortSessions.length === 2) {
            navigate(`/comparison?session1=${selectedCohortSessions[0]}&session2=${selectedCohortSessions[1]}`);
        }
    };

    const handleCompareSearchSessions = () => {
        if (selectedSearchSessions.length === 2) {
            navigate(`/comparison?session1=${selectedSearchSessions[0]}&session2=${selectedSearchSessions[1]}`);
        }
    };

    const isSessionsCompareDisabled = selectedSessions.length !== 2;
    const isCohortsCompareDisabled = selectedCohorts.length !== 2;
    const isCohortSessionsCompareDisabled = selectedCohortSessions.length !== 2;
    const isSearchSessionsCompareDisabled = selectedSearchSessions.length !== 2;

    // Treat a cached-but-stale error as "still loading" while a background
    // refetch is in flight, so switching to a cohort whose sessions query
    // previously errored (e.g. a transient 401) doesn't flash an error
    // message right before the retry succeeds. Only show the error panel
    // once the query has actually settled on an error.
    const cohortSessionsShowLoading = cohortSessionsLoading || (!!cohortSessionsError && cohortSessionsFetching);
    const cohortSessionsShowError = !!cohortSessionsError && !cohortSessionsFetching;

    // Session comparison tab content
    const sessionsTabContent = (
        completedSessions?.length === 0 ? (
                <Card className="mb-4 w-full">
                    <div className="text-center py-8 text-gray-500">
                        <p>No completed sessions found. Complete some sessions first to use the comparison feature.</p>
                    </div>
                </Card>
            ) : (
                <Row gutter={16}>
                    <Col span={16}>
                        <Card className="mb-4 h-full">
                            <h3 className="text-md font-semibold mb-3">
                                Available Sessions
                                <span className="text-sm font-normal text-gray-500 ml-2">
                                    (Select up to 2 sessions)
                                </span>
                            </h3>
                            <div
                                className="ag-theme-alpine"
                                style={{
                                    height: 500,
                                    width: "100%",
                                    ...agGridThemeVars,
                                }}
                            >
                                <AgGridReact
                                    rowData={completedSessions}
                                    columnDefs={sessionColumnDefs}
                                    defaultColDef={agGridDefaultColDef}
                                    pagination={true}
                                    paginationPageSize={10}
                                    suppressRowSelection={true}
                                />
                            </div>
                        </Card>
                    </Col>

                    <Col span={8}>
                        <Card className="mb-4 h-full">
                            <div className="flex flex-col h-full">
                                <h3 className="text-md font-semibold mb-4">Selected Sessions</h3>

                                <div className="flex-1 mb-4">
                                    {selectedSessions.length === 0 ? (
                                        <div className="text-center py-8 text-gray-500">
                                            <FaCodeCompare className="text-4xl text-gray-300 mb-3 mx-auto" />
                                            <p className="text-sm">No sessions selected</p>
                                            <p className="text-xs text-gray-400">Select up to 2 sessions from the table to compare</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            {selectedSessionDetails.map((session) => (
                                                <div key={session.id} className="p-3 bg-gray-50 rounded-lg border">
                                                    <div className="mb-2">
                                                        <div className="font-medium text-xs mb-1">Session #{session.id}</div>
                                                        <SessionTypeTag isCohortSession={!!session.cohort_id} />
                                                    </div>
                                                    <div className="text-xs text-gray-500">
                                                        {new Date(session.created_at).toLocaleString()}
                                                    </div>
                                                    <div className="text-xs text-gray-500 mt-1">
                                                        {session.completed_count} competencies
                                                    </div>
                                                </div>
                                            ))}

                                            {selectedSessions.length === 1 && (
                                                <div className="p-3 border-2 border-dashed border-gray-300 rounded-lg text-center">
                                                    <span className="text-gray-500 text-sm">Select second session</span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <Button
                                    type="primary"
                                    icon={<FaCodeCompare />}
                                    disabled={isSessionsCompareDisabled}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleCompareSessions();
                                    }}
                                    size="large"
                                    className="w-full"
                                >
                                    Compare Sessions
                                </Button>
                            </div>
                        </Card>
                    </Col>
                </Row>
        )
    );

    // Cohort comparison tab content
    const cohortsTabContent = (
        viewingCohortSessions ? (
            <div>
                {cohortSessionsShowLoading ? (
                    <Row gutter={16}>
                        <Col span={16}>
                            <Card className="mb-4 h-full">
                                <div className="flex items-center mb-3">
                                    <h3 className="text-md font-semibold m-0 mr-3">
                                        {selectedCohortForSessions?.name} Sessions
                                        <span className="text-sm font-normal text-gray-500 ml-2">
                                            (Select up to 2 sessions)
                                        </span>
                                    </h3>
                                    <Button
                                        type="default"
                                        size="small"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleBackToCohorts();
                                        }}
                                    >
                                        ← Back
                                    </Button>
                                </div>
                                <div className="text-center py-8 text-gray-500">
                                    <p>Loading sessions...</p>
                                </div>
                            </Card>
                        </Col>
                        <Col span={8}>
                            <Card className="mb-4 h-full">
                                <div className="flex flex-col h-full">
                                    <h3 className="text-md font-semibold mb-4">Selected Sessions</h3>
                                    <div className="flex-1 mb-4">
                                        <div className="text-center py-8 text-gray-500">
                                            <FaCodeCompare className="text-4xl text-gray-300 mb-3 mx-auto" />
                                            <p className="text-sm">Loading...</p>
                                        </div>
                                    </div>
                                </div>
                            </Card>
                        </Col>
                    </Row>
                ) : cohortSessionsShowError ? (
                    <Row gutter={16}>
                        <Col span={16}>
                            <Card className="mb-4 h-full">
                                <div className="flex items-center mb-3">
                                    <h3 className="text-md font-semibold m-0 mr-3">
                                        {selectedCohortForSessions?.name} Sessions
                                    </h3>
                                    <Button
                                        type="default"
                                        size="small"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleBackToCohorts();
                                        }}
                                    >
                                        ← Back
                                    </Button>
                                </div>
                                <div className="text-center py-8 text-gray-500">
                                    <p>Couldn't load sessions for this cohort.</p>
                                    <Button
                                        size="small"
                                        className="mt-2"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            refetchCohortSessions();
                                        }}
                                    >
                                        Retry
                                    </Button>
                                </div>
                            </Card>
                        </Col>
                        <Col span={8}>
                            <Card className="mb-4 h-full">
                                <div className="flex flex-col h-full">
                                    <h3 className="text-md font-semibold mb-4">Selected Sessions</h3>
                                    <div className="flex-1 mb-4">
                                        <div className="text-center py-8 text-gray-500">
                                            <FaCodeCompare className="text-4xl text-gray-300 mb-3 mx-auto" />
                                            <p className="text-sm">No sessions selected</p>
                                        </div>
                                    </div>
                                </div>
                            </Card>
                        </Col>
                    </Row>
                ) : (
                    <Row gutter={16}>
                        <Col span={16}>
                            <Card className="mb-4 h-full">
                                <div className="flex items-center mb-3">
                                    <h3 className="text-md font-semibold m-0 mr-3">
                                        {selectedCohortForSessions?.name} Sessions
                                        <span className="text-sm font-normal text-gray-500 ml-2">
                                            (Select up to 2 sessions)
                                        </span>
                                    </h3>
                                    <Button
                                        type="default"
                                        size="small"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleBackToCohorts();
                                        }}
                                    >
                                        ← Back
                                    </Button>
                                </div>
                                {cohortSessionsData?.length === 0 ? (
                                    <div className="text-center py-8 text-gray-500">
                                        <p>No sessions found in this cohort.</p>
                                    </div>
                                ) : (
                                    <div
                                        className="ag-theme-alpine w-full"
                                        style={{
                                            height: 400,
                                            ...agGridThemeVars,
                                        }}
                                    >
                                        <AgGridReact
                                            rowData={cohortSessionsData}
                                            columnDefs={cohortSessionColumnDefs}
                                            pagination={true}
                                            paginationPageSize={10}
                                            defaultColDef={agGridDefaultColDef}
                                            sortingOrder={["desc", "asc", null]}
                                            initialSort={[
                                                {
                                                    colId: "created_at",
                                                    sort: "desc",
                                                },
                                            ]}
                                            suppressRowSelection={true}
                                        />
                                    </div>
                                )}
                            </Card>
                        </Col>

                        <Col span={8}>
                            <Card className="mb-4 h-full">
                                <div className="flex flex-col h-full">
                                    <h3 className="text-md font-semibold mb-4">Selected Sessions</h3>

                                    <div className="flex-1 mb-4">
                                        {selectedCohortSessions.length === 0 ? (
                                            <div className="text-center py-8 text-gray-500">
                                                <FaCodeCompare className="text-4xl text-gray-300 mb-3 mx-auto" />
                                                <p className="text-sm">No sessions selected</p>
                                                <p className="text-xs text-gray-400">Select up to 2 sessions from the table to compare</p>
                                            </div>
                                        ) : (
                                            <div className="space-y-3">
                                                {selectedCohortSessionDetails.map((session) => (
                                                    <div key={session.id} className="p-3 bg-gray-50 rounded-lg border">
                                                        <div className="mb-2">
                                                            <div className="font-medium text-xs mb-1">{session.username}</div>
                                                            <div className="text-sm font-medium">Session #{session.id}</div>
                                                        </div>
                                                        <div className="text-xs text-gray-500">
                                                            {new Date(session.created_at).toLocaleString()}
                                                        </div>
                                                        <div className="text-xs text-gray-500 mt-1">
                                                            {session.completed_competencies} / {totalCompetenciesInDb} competencies
                                                        </div>
                                                        <div className="text-xs text-gray-500 mt-1">
                                                            <Tag color={session.is_completed ? "green" : "gold"} className="!text-xs">
                                                                {session.is_completed ? "Completed" : "In Progress"}
                                                            </Tag>
                                                        </div>
                                                    </div>
                                                ))}

                                                {selectedCohortSessions.length === 1 && (
                                                    <div className="p-3 border-2 border-dashed border-gray-300 rounded-lg text-center">
                                                        <span className="text-gray-500 text-sm">Select second session</span>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    <Button
                                        type="primary"
                                        icon={<FaCodeCompare />}
                                        disabled={isCohortSessionsCompareDisabled}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleCompareCohortSessions();
                                        }}
                                        size="large"
                                        className="w-full"
                                    >
                                        Compare Sessions
                                    </Button>
                                </div>
                            </Card>
                        </Col>
                    </Row>
                )}
            </div>
        ) : (
            completedCohorts?.length === 0 ? (
                <Card className="mb-4 w-full">
                    <div className="text-center py-8 text-gray-500">
                        <p>No archived cohorts found. Archived cohorts will appear here for comparison.</p>
                    </div>
                </Card>
            ) : (
                <Row gutter={16}>
                    <Col span={16}>
                        <Card className="mb-4 h-full">
                            <h3 className="text-md font-semibold mb-3">
                                Archived Cohorts
                                <span className="text-sm font-normal text-gray-500 ml-2">
                                    (Click "Select Sessions" to view sessions in a cohort)
                                </span>
                            </h3>
                            <div
                                className="ag-theme-alpine"
                                style={{
                                    height: 500,
                                    width: "100%",
                                    ...agGridThemeVars,
                                }}
                            >
                                <AgGridReact
                                    rowData={completedCohorts}
                                    columnDefs={cohortColumnDefs}
                                    defaultColDef={agGridDefaultColDef}
                                    pagination={true}
                                    paginationPageSize={10}
                                    suppressRowSelection={true}
                                />
                            </div>
                        </Card>
                    </Col>

                    <Col span={8}>
                        <Card className="mb-4 h-full">
                            <div className="flex flex-col h-full">
                                <h3 className="text-md font-semibold mb-4">Instructions</h3>

                                <div className="flex-1 mb-4">
                                    <div className="text-center py-8 text-gray-500">
                                        <FaCodeCompare className="text-4xl text-gray-300 mb-3 mx-auto" />
                                        <p className="text-sm">Select a cohort</p>
                                        <p className="text-xs text-gray-400">Click "Select Sessions" on any cohort to view and compare its sessions</p>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </Col>
                </Row>
            )
        )
    );

    // Participant username search tab content (across all archived cohorts)
    const searchTabContent = (
        <Row gutter={16}>
            <Col span={16}>
                <Card className="mb-4 h-full">
                    <h3 className="text-md font-semibold mb-3">
                        Search by participant username
                        <span className="text-sm font-normal text-gray-500 ml-2">
                            (Searches sessions across all your archived cohorts)
                        </span>
                    </h3>
                    <div className="flex gap-2 mb-4">
                        <Input
                            placeholder="username"
                            value={searchUsername}
                            onChange={(e) => setSearchUsername(e.target.value)}
                            onPressEnter={() => handleSearchByUsername()}
                            allowClear
                        />
                        <Button
                            type="primary"
                            loading={searchArchivedSessions.isPending}
                            disabled={!searchUsername.trim()}
                            onClick={() => handleSearchByUsername()}
                        >
                            Search
                        </Button>
                    </div>

                    {searchResults === null ? (
                        <div className="text-center py-8 text-gray-500">
                            <p>Enter a username above to find a participant's sessions across your archived cohorts, or click "Find in other cohorts" next to a participant in the Compare Cohort Sessions tab.</p>
                        </div>
                    ) : searchResults.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            <p>No sessions found for that username in your archived cohorts.</p>
                        </div>
                    ) : (
                        <div
                            className="ag-theme-alpine w-full"
                            style={{
                                height: 400,
                                ...agGridThemeVars,
                            }}
                        >
                            <AgGridReact
                                rowData={searchResults}
                                columnDefs={searchSessionColumnDefs}
                                pagination={true}
                                paginationPageSize={10}
                                defaultColDef={agGridDefaultColDef}
                                sortingOrder={["desc", "asc", null]}
                                initialSort={[{ colId: "created_at", sort: "desc" }]}
                                suppressRowSelection={true}
                            />
                        </div>
                    )}
                </Card>
            </Col>

            <Col span={8}>
                <Card className="mb-4 h-full">
                    <div className="flex flex-col h-full">
                        <h3 className="text-md font-semibold mb-4">Selected Sessions</h3>

                        <div className="flex-1 mb-4">
                            {selectedSearchSessions.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                    <FaCodeCompare className="text-4xl text-gray-300 mb-3 mx-auto" />
                                    <p className="text-sm">No sessions selected</p>
                                    <p className="text-xs text-gray-400">Select up to 2 sessions from the results to compare</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {selectedSearchSessionDetails.map((session) => (
                                        <div key={session.id} className="p-3 bg-gray-50 rounded-lg border">
                                            <div className="mb-2">
                                                <div className="font-medium text-xs mb-1">{session.username}</div>
                                                <div className="text-sm font-medium">{session.cohort_name || "Untitled Cohort"}</div>
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                {new Date(session.created_at).toLocaleString()}
                                            </div>
                                            <div className="text-xs text-gray-500 mt-1">
                                                {session.completed_competencies} / {totalCompetenciesInDb} competencies
                                            </div>
                                            <div className="text-xs text-gray-500 mt-1">
                                                <Tag color={session.is_completed ? "green" : "gold"} className="!text-xs">
                                                    {session.is_completed ? "Completed" : "In Progress"}
                                                </Tag>
                                            </div>
                                        </div>
                                    ))}

                                    {selectedSearchSessions.length === 1 && (
                                        <div className="p-3 border-2 border-dashed border-gray-300 rounded-lg text-center">
                                            <span className="text-gray-500 text-sm">Select second session</span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <Button
                            type="primary"
                            icon={<FaCodeCompare />}
                            disabled={isSearchSessionsCompareDisabled}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleCompareSearchSessions();
                            }}
                            size="large"
                            className="w-full"
                        >
                            Compare Sessions
                        </Button>
                    </div>
                </Card>
            </Col>
        </Row>
    );

    return (
        <div className="py-6 px-10">
            <PageBanner
                icon={<FaCodeCompare />}
                title="Compare Cohorts"
                description="Compare multiple completed sessions or cohorts to track your progress and identify areas of improvement across different competency mappings."
            />

            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={[
                    {
                        key: "sessions",
                        label: "Compare My Sessions",
                        children: sessionsTabContent
                    },
                    {
                        key: "cohorts",
                        label: "Compare Cohort Sessions",
                        children: cohortsTabContent
                    },
                    {
                        key: "search",
                        label: "Search Participant",
                        children: searchTabContent
                    }
                ]}
            />
        </div>
    );
}

export default ComparisonList;